'use client';

import { cssVars } from '@/styles/theme';
import { ChevronDown } from 'lucide-react';
import { useLocale } from 'next-intl';
import { useState } from 'react';

interface AccordionItem {
  id: string;
  title: string;
  content: React.ReactNode;
}

interface AccordionProps {
  items: AccordionItem[];
  allowMultiple?: boolean;
  defaultOpen?: string[];
}

export default function Accordion({
  items,
  allowMultiple = false,
  defaultOpen = [],
}: AccordionProps) {
  const locale = useLocale();
  const isRTL = locale === 'ar';
  const [openItems, setOpenItems] = useState<string[]>(defaultOpen);

  const toggleItem = (id: string) => {
    if (allowMultiple) {
      setOpenItems((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
      );
    } else {
      setOpenItems((prev) => (prev.includes(id) ? [] : [id]));
    }
  };

  const isOpen = (id: string) => openItems.includes(id);

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const open = isOpen(item.id);

        return (
          <div
            key={item.id}
            className="overflow-hidden rounded-2xl border-2 transition-all"
            style={{
              backgroundColor: cssVars.neutral.surface,
              borderColor: open ? cssVars.primary.DEFAULT : cssVars.neutral.border,
            }}
          >
            {/* Header */}
            <button
              onClick={() => toggleItem(item.id)}
              className="flex w-full items-center justify-between p-4 text-start transition-all hover:bg-opacity-50"
              style={{
                backgroundColor: open
                  ? `color-mix(in srgb, ${cssVars.primary.DEFAULT} 10%, transparent)`
                  : 'transparent',
              }}
            >
              <span className="font-bold" style={{ color: cssVars.secondary.DEFAULT }}>
                {item.title}
              </span>
              <ChevronDown
                className={`h-5 w-5 transition-transform ${open ? 'rotate-180' : ''}`}
                style={{ color: open ? cssVars.primary.DEFAULT : cssVars.neutral.textSecondary }}
              />
            </button>

            {/* Content */}
            <div
              className={`overflow-hidden transition-all duration-300 ${
                open ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div
                className="border-t p-4"
                style={{ borderColor: cssVars.neutral.border, color: cssVars.neutral.text }}
              >
                {item.content}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
